import { ApiClient } from '../api/ApiClient';
import { chooseAssessmentConfig } from '../config/ChooseAssessmentConfig';
import { SharedScreenName } from '../config/FlowTypes';
import { NervaControlScreenName } from '../config/nerva/NervaControlScreenNames';
import { PAY_CONFIG } from '../config/PayConfig';
import { ActionType } from '../store/ActionTypes';
import { dispatchEntry } from '../store/entry/EntryActions';
import { store } from '../store/Store';
import { isReferred } from '../util/Cohorts';
import { recordError } from '../util/Errors';
import { isDirectToPayRoute } from '../util/RouterUtils';
import { setupForIntegrationTests } from '../util/SetupForIntegrationTests';
import { Experiments } from './experiments/Experiments';
import { resolveEntry } from './ResolveEntry';

/** Where older versions saved the whole store, answers included. */
const LEGACY_STORE_KEY = 'temp-store';

export async function initializeApp(): Promise<void> {
  await setupForIntegrationTests();

  removeLegacyStore();
  if (!isDirectToPayRoute()) logoutUser();

  // Called before the first screen even when not awaited, so localStorage overrides still apply.
  const experimentsInitialized = Experiments.initialize().catch(recordError); // Continue even on errors

  await Promise.all([
    resolveEntry(window.location.search, new ApiClient()).then(dispatchEntry),
    Experiments.isRemoteExperimentActive ? experimentsInitialized : undefined,
  ]);

  store.dispatch({ type: ActionType.SET_PAGE, page: firstFlowConfigPage(), resetHistory: true });
}

export function firstFlowConfigPage(): string {
  if (isDirectToPayRoute()) return PAY_CONFIG.screens[0].screenName;

  const state = store.getState();
  const flowConfig = chooseAssessmentConfig(state);
  if (state.entry.isReengagementLink) {
    // Referred users must sign in before ProgramOverview (the access grant needs their account);
    // Auth flows login → AiLoading → ProgramOverview. Direct users re-enter just before the
    // flow's own US 65+ branching at LiveFreeNow.
    const screenName = isReferred(state)
      ? SharedScreenName.Auth
      : NervaControlScreenName.IfFreeFromIbs;
    if (flowConfig.screens.some((screen) => screen.screenName === screenName)) return screenName;
    // Fall back to the normal flow rather than crash FlowStack on an unknown screen.
    recordError(
      new Error(`medicare-access entry: config '${flowConfig.name}' lacks ${screenName}`)
    );
  }
  return flowConfig.screens[0].screenName;
}

/** Keeps removing it from browsers that still have it. */
function removeLegacyStore(): void {
  try {
    localStorage.removeItem(LEGACY_STORE_KEY);
  } catch (error) {
    // Storage can be blocked; that mustn't stop the app from starting.
    recordError(error);
  }
}

function logoutUser(): void {
  store.dispatch({ type: ActionType.LOGOUT_USER });
}
